const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const KLAVIYO_REVISION = '2026-07-15';

class NotifyMeForm extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-notify-open]');
    this.panel = this.querySelector('[data-notify-panel]');
    this.input = this.querySelector('[data-notify-email]');
    this.submitButton = this.querySelector('[data-notify-submit]');
    this.message = this.querySelector('[data-notify-message]');

    if (!(this.trigger && this.panel && this.input && this.submitButton && this.message)) {
      return;
    }

    this.pending = false;
    this.abortController = new AbortController();

    const { signal } = this.abortController;

    this.trigger.addEventListener('click', () => this.reveal(), { signal });
    this.submitButton.addEventListener('click', () => this.subscribe(), { signal });
    this.input.addEventListener('input', () => this.clearMessage(), { signal });
    this.input.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Enter') {
          return;
        }

        event.preventDefault();
        this.subscribe();
      },
      { signal },
    );
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  reveal() {
    this.trigger.hidden = true;
    this.panel.hidden = false;
    this.input.focus();
  }

  clearMessage() {
    this.message.hidden = true;
    this.message.textContent = '';
    this.message.classList.remove('notify-me__message--error', 'notify-me__message--success');
    this.input.removeAttribute('aria-invalid');
  }

  showMessage(text, state) {
    this.message.textContent = text;
    this.message.classList.toggle('notify-me__message--error', state === 'error');
    this.message.classList.toggle('notify-me__message--success', state === 'success');
    this.message.hidden = false;
  }

  validate() {
    const email = this.input.value.trim();

    if (email === '') {
      return { email, error: this.dataset.emptyMessage };
    }

    if (!this.input.checkValidity()) {
      return { email, error: this.dataset.invalidMessage };
    }

    return { email, error: null };
  }

  async subscribe() {
    if (this.pending) {
      return;
    }

    const { email, error } = this.validate();

    if (error) {
      this.input.setAttribute('aria-invalid', 'true');
      this.showMessage(error, 'error');
      this.input.focus();
      return;
    }

    this.pending = true;
    this.submitButton.disabled = true;
    this.submitButton.setAttribute('aria-busy', 'true');
    this.clearMessage();

    try {
      const response = await fetch(`${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(this.dataset.companyId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email },
                },
              },
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${this.dataset.variantId}`,
                },
              },
            },
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Klaviyo responded with ${response.status}`);
      }

      this.panel.hidden = true;
      this.showMessage(this.dataset.successMessage, 'success');
    } catch {
      this.showMessage(this.dataset.errorMessage, 'error');
    } finally {
      this.pending = false;
      this.submitButton.disabled = false;
      this.submitButton.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('notify-me-form')) {
  customElements.define('notify-me-form', NotifyMeForm);
}
